<!-- Chat toolbar button offering a dropdown to attach photos, files, or images. -->

<script lang="ts" setup>
import { randomString } from '@nzyme/crypto';
import { isProbablyMobile } from '@nzyme/dom-utils/isProbablyMobile.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { reactive } from '@nzyme/vue-utils/reactivity/reactive.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { useFileDialog } from '@vueuse/core';
import { Camera, Image, Paperclip, Plus } from 'lucide-vue-next';
import { computed } from 'vue';

import type { FileInput } from '@sigma/files-common/FileInput.js';
import { PATIENT_FILE_TYPES } from '@sigma/files-common/patientFileValidation.js';

import Button from '../Button.vue';
import Dropdown from '../dropdown/Dropdown.vue';
import DropdownAction from '../dropdown/DropdownAction.vue';
import * as l from './ChatAttachFileButton.loc.js';

interface Emits {
  upload: [files: FileInput[]];
}

defineEmits<Emits>();
const emitAsync = useEmitAsync<Emits>();

const t = useTranslate();

const hasCamera = computed(() => {
  return 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices && isProbablyMobile();
});

const photos = useFileDialog({
  accept: 'image/*',
  capture: 'environment',
});

const files = useFileDialog({
  accept: [...PATIENT_FILE_TYPES, 'image/*'].join(','),
});

const images = useFileDialog({
  accept: 'image/*',
});

// Attach files and clear the input after upload
photos.onChange(f => {
  void handleUpload(f);
  photos.reset();
});
files.onChange(f => {
  void handleUpload(f);
  files.reset();
});
images.onChange(f => {
  void handleUpload(f);
  images.reset();
});

async function handleUpload(fileList: FileList | null) {
  if (!fileList || fileList.length === 0) {
    return;
  }

  const filesToUpload = Array.from(fileList).map(file =>
    reactive<FileInput>({
      tempId: randomString(16),
      name: file.name,
      type: file.type,
      file,
      progress: 0,
    }),
  );

  // Extract image dimensions for image files
  await Promise.all(
    filesToUpload
      .filter(fileInput => fileInput.type.startsWith('image/'))
      .map(async fileInput => {
        const img = document.createElement('img');
        // We are creating a preview URL for the image so we can display it in the chat.
        // We are not clearing it after, because we still use it for previews in the chat.
        // Real image preview is generated on the server side asynchronously, so we can display preview immediately.
        const url = URL.createObjectURL(fileInput.file);
        await new Promise<void>((resolve, reject) => {
          img.addEventListener('load', () => {
            fileInput.width = img.naturalWidth;
            fileInput.height = img.naturalHeight;
            fileInput.url = url;
            resolve();
          });
          img.addEventListener('error', () => reject(new Error('Failed to load image')));
          img.src = url;
        }).catch(() => {
          // If image fails to load, just continue without dimensions
        });
      }),
  );

  await emitAsync('upload', filesToUpload);
}
</script>

<template>
  <Dropdown align="start">
    <template #trigger>
      <Button
        tid="attach-file-button"
        variant="link"
        :icon="Plus"
        color="black"
        rounded
        :class="css.button"
        :title="t(l.attachFiles)"
        no-wait
      />
    </template>

    <DropdownAction
      v-if="hasCamera"
      :icon="Camera"
      @click="photos.open"
    >
      {{ t(l.takePhoto) }}
    </DropdownAction>

    <DropdownAction
      :icon="Paperclip"
      @click="files.open"
    >
      {{ t(l.attachFile) }}
    </DropdownAction>

    <DropdownAction
      :icon="Image"
      @click="images.open"
    >
      {{ t(l.attachImage) }}
    </DropdownAction>
  </Dropdown>
</template>

<style lang="scss" module="css">
.button {
  --button-padding: 0;
  width: 40px;
  height: 40px;
  font-size: 20px;
}
</style>
